import { useRef, useState, useMemo, useEffect } from "react";
import "./ChangePassword.scss";
import { AuthModal } from "../index";
import { Field, Formik, Form } from "formik";
import {
  Button,
  Input,
  PasswordStrengthChecker,
  PasswordValidation,
} from "shared";
import * as Yup from "yup";
import {
  useGetResetPasswordCode,
  useCheckResetCode,
  useChangeSuccessorPassword,
} from "hooks/successors";
import { EnterCode } from "shared";
import { useSearchParams } from "react-router-dom";

type Step = 1 | 2 | 3;

const SetPassword = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [password, setPassword] = useState<string>("");
  const [step, setStep] = useState<Step>(1);
  const [code, setCode] = useState<string[]>(["", "", "", ""]);
  const [codeError, setCodeError] = useState<string>("");
  const [showValidation, setShowValidation] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const formRef = useRef<any>(null);

  const resetCodeMutation = useGetResetPasswordCode({
    onSuccess: () => {
      setStep(2);
    },
  });

  const checkCodeMutation = useCheckResetCode({
    onSuccess: (data) => {
      if (data.valid) {
        setStep(3);
      }
    },
  });

  const changePasswordMutation = useChangeSuccessorPassword({
    onSuccess: () => {
      const params = new URLSearchParams(searchParams);
      params.set("tab", "docs");
      params.set("page", "1");
      setSearchParams(params);
    },
  });

  useEffect(() => {
    const value = code.join("");
    if (value.length === 4) {
      checkCodeMutation.mutateAsync({ code: value });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const stepConfig = useMemo(() => {
    if (step === 1) {
      return {
        heading: "Zmiana hasła do zakładki “Sukcesja”",
        paragraph: (
          <>
            Aby zainicjować zmianę, należy podać{" "}
            <strong>aktualne hasło.</strong>
            <br />
            <br />
            Po jego poprawnym wprowadzeniu kod weryfikacyjny zostanie wysłany na
            adres e-mail podany przy tworzeniu hasła.
          </>
        ),
        initialValues: { password: "" },
        validation: Yup.object().shape({
          password: Yup.string()
            .required("Pole wymagane")
            .min(8, "Minimum 8 znaków"),
        }),
        submitLabel: "Potwierdź hasło",
        renderFields: (values: any) => (
          <Field
            type="password"
            id="password"
            name="password"
            label="Aktualne hasło"
            as={Input}
          />
        ),
        onSubmit: async (values: any) => {
          setPassword(values.password);
          await resetCodeMutation.mutateAsync({ password: values.password });
        },
      };
    }

    if (step === 2) {
      return {
        heading: "Weryfikacja kodu",
        paragraph: (
          <>
            Na adres e-mail podany przy tworzeniu hasła do zakładki „Sukcesja”
            został wysłany <strong>kod weryfikacyjny.</strong>
            <br />
            <br />
            Proszę wpisać ten kod poniżej, aby przejść do ustawienia nowego
            hasła.
          </>
        ),
        initialValues: { code: "" },
        validation: Yup.object().shape({
          code: Yup.string().required("Pole wymagane"),
        }),
        // submitLabel: "Wyślij kod ponownie",
        renderFields: () => (
          <Form>
            <EnterCode setCode={setCode} code={code} />
            {codeError && (
              <p className="form-error" role="alert" style={{ marginTop: 8 }}>
                {codeError}
              </p>
            )}

            <div className="enter-code-page__actions" style={{ marginTop: 16 }}>
              <p className="login-wrapper__anchor enter-code-page__anchor">
                Nie otrzymałeś kodu?
              </p>
              <Button
                className="successor-auth-modal__button button--stroke"
                styleType="secondary"
                label="Wyślij kod ponownie"
                type="button"
                onClick={async () => {
                  setCode(["", "", "", ""]);
                  setCodeError("");
                  await resetCodeMutation.mutateAsync({ password });
                }}
              />
            </div>
          </Form>
        ),
        onSubmit: async () => {},
      };
    }

    return {
      heading: "Ustawianie nowego hasła",
      paragraph:
        "Poniżej można ustawić nowe hasło do zakładki „Sukcesja”. Nowe hasło będzie wymagane przy każdorazowym dostępie do tej sekcji aplikacji.",
      initialValues: { newPassword: "", confirmPassword: "" },
      validation: Yup.object().shape({
        newPassword: Yup.string()
          .required("Pole wymagane")
          .min(8, "Minimum 8 znaków"),
        confirmPassword: Yup.string()
          .required("Pole wymagane")
          .oneOf([Yup.ref("newPassword")], "Hasła muszą być takie same"),
      }),
      submitLabel: "Potwierdź",
      renderFields: (values: any) => (
        <>
          <Field
            type="password"
            id="newPassword"
            name="newPassword"
            label="Nowe hasło"
            as={Input}
            onFocus={() => setShowValidation(true)}
            onBlur={() => isPasswordValid && setShowValidation(false)}
          />
          <PasswordStrengthChecker
            password={values.newPassword}
            isHidden={!showValidation}
          />
          <PasswordValidation
            show={showValidation}
            password={values.newPassword}
            setIsValid={(value: boolean) => setIsPasswordValid(value)}
          />
          <Field
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            label="Powtórz nowe hasło"
            as={Input}
          />
        </>
      ),
      onSubmit: async (_values: any) => {
        await changePasswordMutation.mutateAsync({
          password: _values.newPassword,
        });
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, resetCodeMutation, codeError, code, password]);

  return (
    <AuthModal
      step={step}
      heading={stepConfig.heading}
      paragraph={stepConfig.paragraph}
    >
      <Formik
        key={step}
        initialValues={stepConfig.initialValues}
        validationSchema={stepConfig.validation}
        onSubmit={stepConfig.onSubmit}
        innerRef={formRef}
        enableReinitialize
      >
        {({ values }) => (
          <Form>
            {stepConfig.renderFields(values)}
            {stepConfig.submitLabel && (
              <Button
                className={`successor-auth-modal__button ${
                  step === 2 ? "button--stroke" : ""
                }`}
                label={stepConfig.submitLabel}
                styleType={`${step === 2 ? "secondary" : "primary"}`}
                type="submit"
                onClick={() => null}
              />
            )}
          </Form>
        )}
      </Formik>
    </AuthModal>
  );
};

export default SetPassword;
